import React, { useCallback, useRef, useEffect, useState } from "react";
import Webcam from "react-webcam";
import { FaceMesh } from "@mediapipe/face_mesh";
import * as cam from "@mediapipe/camera_utils";
import "../bch_integration";
import { useLogs } from "../context/LogContext";
import { api } from "../utils/api";
import { loadString } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";

function VerifyScan() {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const leftArrowImage = useRef(null);
  const rightArrowImage = useRef(null);
  const rollrightArrowImage = useRef(null);
  const rollleftArrowImage = useRef(null);
  const upArrowImage = useRef(null);
  const downArrowImage = useRef(null);

  const { addLog, setChannelMeta } = useLogs();
  const capturedPhotoRef = useRef(false);
  const photoHashRef = useRef(null);
  const workflowLoggedRef = useRef(false);
  const outcomeLoggedRef = useRef({ success: false, failure: false });

  // Load salt from localStorage safely
  const [salt, setSalt] = useState(null);
  // Animation states
  const arrowAnimationRef = useRef({
    pitch: { opacity: 0, scale: 1, pulse: 0 },
    yaw: { opacity: 0, scale: 1, pulse: 0 },
    roll: { opacity: 0, scale: 1, pulse: 0 },
  });
  const centerDotAnimationRef = useRef({
    scale: 1,
    pulsePhase: 0,
    color: { r: 255, g: 0, b: 0 },
  });
  const particlesRef = useRef([]);

  const [center, setCenter] = useState(false);
  const [iod, setIod] = useState(0);
  // NOTE: pitch/yaw/roll/ear state removed (unused; caused Vercel build to fail via eslint no-unused-vars)

  // Verification states
  const [bchReady, setBchReady] = useState(false);
  const [registrationData, setRegistrationData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [capturedDistances, setCapturedDistances] = useState([]); // Array of distance arrays (50 frames)
  const [verificationResult, setVerificationResult] = useState(null);
  const [captureProgress, setCaptureProgress] = useState(0); // Track capture progress (0-50)

  // Use refs for frame capture tracking
  const hasCapturedRef = useRef(false);
  const frameCountRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const dataLoadedRef = useRef(false); // Track if data has been loaded
  const FRAME_INTERVAL = 50; // Capture frame every 100ms (50 frames = 5 seconds)
  const TOTAL_FRAMES = 10;

  const cameraRef = useRef(null);
  const onResultsRef = useRef(null);

  // Smooth animation helper
  const lerp = (start, end, factor) => {
    return start + (end - start) * factor;
  };

  // Update arrow animations smoothly
  const updateArrowAnimation = (type, shouldShow) => {
    const current = arrowAnimationRef.current[type];
    const targetOpacity = shouldShow ? 1 : 0;
    const targetScale = shouldShow ? 1 : 0.8;

    current.opacity = lerp(current.opacity, targetOpacity, 0.15);
    current.scale = lerp(current.scale, targetScale, 0.15);
    current.pulse = shouldShow ? (current.pulse + 0.1) % (Math.PI * 2) : 0;
  };

  // Draw arrow with smooth animation
  const drawAnimatedArrow = (ctx, img, x, y, width, height, animation) => {
    // Check if image is valid and loaded (not broken)
    if (!img || !img.complete || animation.opacity < 0.01) return;

    // Check if image is in broken state (naturalWidth/naturalHeight will be 0 if broken)
    if (img.naturalWidth === 0 || img.naturalHeight === 0) return;

    ctx.save();
    ctx.globalAlpha = animation.opacity;

    const pulseScale = 1 + Math.sin(animation.pulse) * 0.1;
    const finalScale = animation.scale * pulseScale;

    const scaledWidth = width * finalScale;
    const scaledHeight = height * finalScale;
    const offsetX = (width - scaledWidth) / 2;
    const offsetY = (height - scaledHeight) / 2;

    ctx.setTransform(1, 0, 0, -1, 0, 0);
    ctx.drawImage(img, x + offsetX, y + offsetY, scaledWidth, scaledHeight);
    ctx.restore();
  };

  // Update center dot animation
  const updateCenterDotAnimation = (isCentered) => {
    const anim = centerDotAnimationRef.current;
    const targetColor = isCentered
      ? { r: 0, g: 255, b: 0 }
      : { r: 255, g: 0, b: 0 };

    anim.color.r = lerp(anim.color.r, targetColor.r, 0.1);
    anim.color.g = lerp(anim.color.g, targetColor.g, 0.1);
    anim.color.b = lerp(anim.color.b, targetColor.b, 0.1);

    if (isCentered && Math.random() < 0.3) {
      createParticle();
    }
  };

  // Create particles
  const createParticle = () => {
    const angle = Math.random() * Math.PI * 2;
    const speed = 1 + Math.random() * 2;
    particlesRef.current.push({
      x: 0,
      y: 0,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1.0,
      size: 2 + Math.random() * 3,
    });

    if (particlesRef.current.length > 50) {
      particlesRef.current = particlesRef.current.slice(-50);
    }
  };

  // Draw particles
  const drawParticles = (ctx, cx, cy) => {
    particlesRef.current = particlesRef.current.filter((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.life -= 0.02;

      if (p.life <= 0) return false;

      ctx.save();
      ctx.globalAlpha = p.life;
      ctx.fillStyle = "#00ff00";
      ctx.shadowColor = "#00ff00";
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(cx + p.x, cy + p.y, p.size, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();

      return true;
    });
  };

  // Draw center dot with animation
  const drawCenterDot = (ctx, cx, cy) => {
    const anim = centerDotAnimationRef.current;

    ctx.save();

    const gradient = ctx.createRadialGradient(
      cx,
      cy,
      0,
      cx,
      cy,
      8 * anim.scale
    );
    const color = `rgb(${Math.round(anim.color.r)}, ${Math.round(
      anim.color.g
    )}, ${Math.round(anim.color.b)})`;
    gradient.addColorStop(0, color);
    gradient.addColorStop(
      0.5,
      `rgba(${Math.round(anim.color.r)}, ${Math.round(
        anim.color.g
      )}, ${Math.round(anim.color.b)}, 0.3)`
    );
    gradient.addColorStop(1, "transparent");

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(cx, cy, 8 * anim.scale, 0, 2 * Math.PI);
    ctx.fill();

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx, cy, 5 * anim.scale, 0, 2 * Math.PI);
    ctx.fill();

    ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
    ctx.beginPath();
    ctx.arc(cx - 1.5, cy - 1.5, 1.5, 0, 2 * Math.PI);
    ctx.fill();

    ctx.restore();
  };

  // Draw face landmarks
  const drawFaceLandmarks = (ctx, landmarks, opacity = 1) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.fillStyle = "white";
    ctx.shadowColor = "rgba(0, 188, 212, 0.8)";
    ctx.shadowBlur = 8;

    landmarks.forEach((point) => {
      ctx.beginPath();
      ctx.arc(point.x * -75, point.y * 100, 2.5, 0, 2 * Math.PI);
      ctx.fill();
    });

    ctx.restore();
  };

  // Draw connection lines between landmarks
  const drawFaceConnections = (ctx, landmarks) => {
    ctx.save();
    ctx.strokeStyle = "rgba(0, 188, 212, 0.3)";
    ctx.lineWidth = 1;
    ctx.shadowColor = "rgba(0, 188, 212, 0.5)";
    ctx.shadowBlur = 4;

    ctx.beginPath();
    for (let i = 0; i < landmarks.length - 1; i++) {
      ctx.moveTo(landmarks[i].x * -75, landmarks[i].y * 100);
      ctx.lineTo(landmarks[i + 1].x * -75, landmarks[i + 1].y * 100);
    }
    ctx.moveTo(
      landmarks[landmarks.length - 1].x * -75,
      landmarks[landmarks.length - 1].y * 100
    );
    ctx.lineTo(landmarks[0].x * -75, landmarks[0].y * 100);
    ctx.stroke();

    ctx.restore();
  };

  function isNoseCentered(results) {
    if (
      !canvasRef.current ||
      !results.multiFaceLandmarks ||
      results.multiFaceLandmarks.length === 0
    )
      return false;
    const cx = canvasRef.current.width / 2;
    const cy = canvasRef.current.height / 2;
    const nose = results.multiFaceLandmarks[0][4];

    const nx = nose.x * canvasRef.current.width;
    const ny = (1 - nose.y) * canvasRef.current.height;
    const distPix = Math.sqrt((nx - cx) ** 2 + (ny - cy) ** 2);

    return distPix <= 10;
  }

  function isPoseStable(pitch, yaw, roll) {
    return (
      Math.abs(pitch) <= 0.1 && Math.abs(yaw) <= 0.1 && Math.abs(roll) <= 0.1
    );
  }

  function computeIOD(pixelLandmarks) {
    const leftEyeOuter = pixelLandmarks[33];
    const rightEyeOuter = pixelLandmarks[263];

    const dx = rightEyeOuter.x - leftEyeOuter.x;
    const dy = rightEyeOuter.y - leftEyeOuter.y;

    const iod = Math.sqrt(dx * dx + dy * dy);
    setIod(iod);
    return iod >= 130 && iod <= 133;
  }

  function computePoseAngles(noseTipPoint, verticalLinePoint) {
    const { x, y } = noseTipPoint;
    const den = Math.sqrt(Math.max(0.0, 1.0 - y * y - x * x));

    const pitch = Math.atan2(y, den);
    const yaw = Math.atan2(x, den);
    const roll = Math.atan2(verticalLinePoint[1], verticalLinePoint[0]);

    return { pitch, yaw, roll };
  }

  const calculateDistance = (point1, point2) => {
    const x1 = point1.x,
      y1 = point1.y;
    const x2 = point2.x,
      y2 = point2.y;
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  };

  const calculateLandmarkDistances = (
    normalizedPoints,
    mandatoryLandmarkPairs
  ) => {
    return mandatoryLandmarkPairs.map((pair) => {
      const point1 = normalizedPoints[pair[0]];
      const point2 = normalizedPoints[pair[1]];
      return calculateDistance(point1, point2);
    });
  };

  function drawScatterPlot(
    ctx,
    landmarks,
    pitch,
    yaw,
    roll,
    results,
    { color = "white", radius = 2, showIndex = false } = {}
  ) {
    const canvas = canvasRef.current;
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    const cx = width / 2;
    const cy = height / 2;

    let isCentered = false;
    if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
      const nose = results.multiFaceLandmarks[0][4];
      const nx = nose.x * width;
      const ny = (1 - nose.y) * height;
      const distPix = Math.sqrt((nx - cx) ** 2 + (ny - cy) ** 2);
      isCentered = distPix <= 10;
    }

    setCenter(isCentered);
    updateCenterDotAnimation(isCentered);

    ctx.fillStyle = "transparent";
    ctx.fillRect(0, 0, width, height);

    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.scale(1, -1);

    if (landmarks) {
      drawFaceConnections(ctx, landmarks);
      drawFaceLandmarks(ctx, landmarks, 0.9);
    }

    updateArrowAnimation("pitch", Math.abs(pitch) > 0.1);
    updateArrowAnimation("yaw", Math.abs(yaw) > 0.1);
    updateArrowAnimation("roll", Math.abs(roll) > 0.1);

    if (pitch > 0.1) {
      drawAnimatedArrow(
        ctx,
        upArrowImage.current,
        260,
        -380,
        100,
        100,
        arrowAnimationRef.current.pitch
      );
    } else if (pitch < -0.1) {
      drawAnimatedArrow(
        ctx,
        downArrowImage.current,
        280,
        -180,
        100,
        100,
        arrowAnimationRef.current.pitch
      );
    }

    if (yaw > 0.1) {
      drawAnimatedArrow(
        ctx,
        rightArrowImage.current,
        380,
        -280,
        100,
        100,
        arrowAnimationRef.current.yaw
      );
    } else if (yaw < -0.1) {
      drawAnimatedArrow(
        ctx,
        leftArrowImage.current,
        180,
        -280,
        100,
        100,
        arrowAnimationRef.current.yaw
      );
    }

    if (roll > 0.1) {
      drawAnimatedArrow(
        ctx,
        rollleftArrowImage.current,
        380,
        -220,
        100,
        100,
        arrowAnimationRef.current.roll
      );
    } else if (roll < -0.1) {
      drawAnimatedArrow(
        ctx,
        rollrightArrowImage.current,
        180,
        -220,
        100,
        100,
        arrowAnimationRef.current.roll
      );
    }

    ctx.restore();

    drawParticles(ctx, cx, cy);
    drawCenterDot(ctx, cx, cy);
  }

  // Process verification with captured distances (array of frames)
  // Processes frames sequentially, updating result after each frame
  const processVerification = useCallback(async (distancesArray) => {
    // console.log(
    //   "🚀 processVerification called with",
    //   distancesArray?.length || 0,
    //   "captured frames"
    // );

    if (!bchReady) {
      console.error("❌ BCH not ready - aborting verification");
      setIsProcessing(false);
      return;
    }

    if (!registrationData || registrationData.length === 0) {
      console.error("❌ No registration data - aborting verification");
      setIsProcessing(false);
      return;
    }

    if (!distancesArray || distancesArray.length === 0) {
      console.error("❌ No captured distances - aborting verification");
      setIsProcessing(false);
      return;
    }

    // console.log(
    //   "✅ Starting verification: Processing ALL",
    //   distancesArray.length,
    //   "frames against",
    //   registrationData.length,
    //   "registration record(s)"
    // );
    // addLog(
    //   `🔄 Starting verification: Processing ${distancesArray.length} frame(s) against ${registrationData.length} face registration(s). Verifying against ALL faces.`
    // );
    // addLog(
    //   `📋 Verification strategy: Each frame will be checked against ALL ${registrationData.length} face registration(s) to find the best match.`
    // );

    try {
      const allFrameResults = []; // Results for each frame
      const BATCH_SIZE = 1; // Process 1 frame at a time so we can redirect ASAP on first match

      // Helper function to process a single frame
      const processFrame = async (frameIndex, frameDistances) => {
        // console.log(
        //   `\n📊 Processing frame ${frameIndex + 1}/${distancesArray.length} (${
        //     frameDistances.length
        //   } distances)`
        // );

        const frameResults = [];

        // For each frame, verify against ALL registration data (all faces)
        // addLog(
        //   `🔍 Frame ${frameIndex + 1}: Verifying against ${registrationData.length
        //   } face registration(s)...`
        // );

        for (let regIndex = 0; regIndex < registrationData.length; regIndex++) {
          const regData = registrationData[regIndex];
          // console.log(
          //   `  🔍 Matching frame ${frameIndex + 1} against registration ${
          //     regIndex + 1
          //   }/${registrationData.length}`
          // );

          try {
            // Call verifyBiometric with this frame's distances and registration object
            // This verifies against each face registration record
            const result = await window.BCHBiometric.verifyBiometric(
              frameDistances,
              regData, // Pass the registration object (contains token, k2, helper)
              salt,
              regIndex
            );

            // console.log(
            //   `  ✅ Frame ${frameIndex + 1}, reg ${regIndex + 1} result:`,
            //   {
            //     success: result.success,
            //     matchPercentage: result.matchPercentage?.toFixed(2) + "%",
            //     errorPercentage: result.errorPercentage?.toFixed(2) + "%",
            //     numErrors: result.numErrors,
            //     hashBitsSimilarity:
            //       result.hashBitsSimilarity?.toFixed(2) + "%" || "N/A",
            //   }
            // );

            // Log if this is a high match percentage
            if (result.matchPercentage >= 80) {
              // console.log(
              //   `  ⚠️ High match detected: ${result.matchPercentage.toFixed(
              //     2
              //   )}% (but not 100%)`
              // );
            }

            frameResults.push({
              ...result,
              frameIndex: frameIndex,
              registrationIndex: regIndex,
              registrationId: regData.id || regIndex,
            });
          } catch (error) {
            // console.error(
            //   `  Error verifying frame ${frameIndex + 1} against registration ${
            //     regIndex + 1
            //   }:`,
            //   error
            // );
            frameResults.push({
              success: false,
              matchPercentage: 0,
              numErrors: -1,
              frameIndex: frameIndex,
              registrationIndex: regIndex,
              error: error.message,
            });
          }
        }

        // Find best match for this frame (even if not successful, get the highest percentage)
        const bestMatchForFrame = frameResults.reduce((best, current) => {
          if (!best) return current;
          if (!current) return best;
          // Compare match percentages - get the highest one
          const bestPercentage = best.matchPercentage || 0;
          const currentPercentage = current.matchPercentage || 0;
          if (currentPercentage > bestPercentage) {
            return current;
          }
          return best;
        }, null);

        if (bestMatchForFrame) {
          // console.log(`  🎯 Frame ${frameIndex + 1} best match:`, {
          //   success: bestMatchForFrame.success,
          //   matchPercentage:
          //     bestMatchForFrame.matchPercentage?.toFixed(2) + "%",
          //   registrationIndex: bestMatchForFrame.registrationIndex,
          //   numErrors: bestMatchForFrame.numErrors,
          //   errorPercentage:
          //     bestMatchForFrame.errorPercentage?.toFixed(2) + "%",
          // });

          // Log if 100% match found
          if (bestMatchForFrame.matchPercentage === 100) {
            // console.log(
            //   `  🎉 PERFECT 100% MATCH FOUND for frame ${frameIndex + 1}!`
            // );
            // addLog(
            //   `🎉 Frame ${
            //     frameIndex + 1
            //   }: Perfect 100% match with face registration ${
            //     bestMatchForFrame.registrationIndex + 1
            //   }!`
            //   ,
            //   "apple_face_id"
            // );
          } else {
            //   addLog(
            //     `📊 Frame ${
            //       frameIndex + 1
            //     }: Best match ${bestMatchForFrame.matchPercentage.toFixed(
            //       1
            //     )}% with face registration ${
            //       bestMatchForFrame.registrationIndex + 1
            //     } (checked ${frameResults.length} face(s))`
            //     ,
            //     "apple_face_id"
            //   );
          }
        } else {
          // console.log(`  ❌ No match found for frame ${frameIndex + 1}`);
          // addLog(
          //   `❌ Frame ${frameIndex + 1}: No match found (checked ${
          //     frameResults.length
          //   } face registration(s))`
          //   ,
          //   "apple_face_id"
          // );
        }

        return {
          frameIndex: frameIndex,
          bestMatch: bestMatchForFrame,
          allMatches: frameResults,
        };
      };

      // Helper function to update result
      const updateResult = (frameResults, isComplete = false) => {
        const processedFrames = frameResults.length;
        const successfulFrames = frameResults.filter(
          (frameResult) =>
            frameResult.bestMatch && frameResult.bestMatch.success
        ).length;

        const successRate =
          processedFrames > 0 ? (successfulFrames / processedFrames) * 100 : 0;

        // Find the overall best match from all processed frames so far
        const currentBestMatch = frameResults.reduce((best, current) => {
          if (!current || !current.bestMatch) return best;
          if (!best || !best.bestMatch) return current;
          // Compare match percentages
          const bestPercentage = best.bestMatch.matchPercentage || 0;
          const currentPercentage = current.bestMatch.matchPercentage || 0;
          if (currentPercentage > bestPercentage) {
            return current;
          }
          return best;
        }, null);

        const result = {
          success: false, // Will be determined at the end if complete
          successRate: successRate,
          successfulFrames: successfulFrames,
          totalFrames: distancesArray.length,
          processedFrames: processedFrames,
          bestMatch: currentBestMatch?.bestMatch || null,
          allFrameResults: frameResults,
          isComplete: isComplete,
        };

        setVerificationResult(result);
        return result;
      };

      // Track if we found a 100% match (but still process all frames)
      let foundPerfectMatch = false;

      // Process frames in batches of 2, yielding to background after each batch
      for (
        let batchStart = 0;
        batchStart < distancesArray.length;
        batchStart += BATCH_SIZE
      ) {
        const batchEnd = Math.min(
          batchStart + BATCH_SIZE,
          distancesArray.length
        );
        // console.log(
        //   `\n🔄 Processing batch: frames ${batchStart + 1}-${batchEnd}/${
        //     distancesArray.length
        //   }`
        // );

        // Process 2 frames in parallel (or remaining frames if less than 2)
        const batchPromises = [];
        for (let i = batchStart; i < batchEnd; i++) {
          batchPromises.push(processFrame(i, distancesArray[i]));
        }

        // Wait for batch to complete
        const batchResults = await Promise.all(batchPromises);
        allFrameResults.push(...batchResults);

        // NEW: if ANY frame succeeds, stop processing and redirect immediately
        for (const frameResult of batchResults) {
          if (frameResult?.bestMatch?.success) {
            // foundAnySuccessfulFrame = true;
            const currentResult = updateResult(allFrameResults, true);
            setVerificationResult({
              ...currentResult,
              success: true,
              isComplete: true,
              bestMatch: frameResult.bestMatch,
            });
            return; // stop processing remaining frames
          }
        }

        // Check if we found a 100% match in this batch
        for (const frameResult of batchResults) {
          if (
            frameResult.bestMatch &&
            frameResult.bestMatch.matchPercentage === 100
          ) {
            foundPerfectMatch = true;
            // console.log(
            //   `🎯 Perfect 100% match found at frame ${
            //     frameResult.frameIndex + 1
            //   }! Continuing to process all frames...`
            // );
            // addLog(
            //   `✨ Perfect match (100%) detected at frame ${
            //     frameResult.frameIndex + 1
            //   }! Processing remaining frames...`
            // );
          }
        }

        // Update result after each batch
        const currentResult = updateResult(allFrameResults, false);

        // If we found 100% match, update success status early (but keep processing)
        if (foundPerfectMatch && currentResult.bestMatch) {
          setVerificationResult({
            ...currentResult,
            success: true, // Mark as successful early
            isComplete: false, // Still processing
          });
        }

        // Yield to background after each batch (allow UI to update)
        // Use requestAnimationFrame for smoother UI updates
        if (batchEnd < distancesArray.length) {
          // Yield to allow UI to render the progress
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              setTimeout(resolve, 10); // Small delay to ensure UI updates
            });
          });
        }
      }

      // Calculate final verification result after all frames are processed
      const finalResult = updateResult(allFrameResults, true);

      // Log completion
      // console.log(
      //   `\n✅ Completed processing all ${distancesArray.length} frames`
      // );
      addLog(`✅ Completed processing all ${distancesArray.length} frame(s)`);

      // Calculate overall success
      const successfulFrames = allFrameResults.filter(
        (frameResult) => frameResult.bestMatch && frameResult.bestMatch.success
      ).length;

      const successRate = (successfulFrames / distancesArray.length) * 100;
      // Success if best match is 100% OR if 50%+ of frames match
      const bestMatchPercentage = finalResult.bestMatch?.matchPercentage || 0;
      const overallSuccess = bestMatchPercentage === 100 || successRate >= 50;

      // Update final result with success status
      const finalResultWithSuccess = {
        ...finalResult,
        success: overallSuccess,
      };

      setVerificationResult(finalResultWithSuccess);

      // console.log("🎯 Final verification summary:", {
      //   totalFrames: distancesArray.length,
      //   successfulFrames: successfulFrames,
      //   successRate: successRate,
      //   overallSuccess: overallSuccess,
      //   bestMatch: finalResultWithSuccess.bestMatch
      //     ? {
      //         matchPercentage: finalResultWithSuccess.bestMatch.matchPercentage,
      //         success: finalResultWithSuccess.bestMatch.success,
      //         registrationIndex:
      //           finalResultWithSuccess.bestMatch.registrationIndex,
      //       }
      //     : null,
      // });

      // Log final result only
      if (finalResultWithSuccess.success && finalResultWithSuccess.bestMatch) {
        const bestPercentage =
          finalResultWithSuccess.bestMatch.matchPercentage.toFixed(2);
        if (bestPercentage === "100.00") {
          // addLog(
          //   `✅ Verification SUCCESS - Perfect match (100%) achieved! ` +
          //     `${successfulFrames}/${
          //       distancesArray.length
          //     } frames matched (${successRate.toFixed(1)}%), ` +
          //     `Errors Corrected: ${finalResultWithSuccess.bestMatch.numErrors}, ` +
          //     `Registration Index: ${finalResultWithSuccess.bestMatch.registrationIndex}`
          // );
        } else {
          // addLog(
          //   `✅ Verification SUCCESS - ${successfulFrames}/${
          //     distancesArray.length
          //   } frames matched (${successRate.toFixed(1)}%), ` +
          //     `Best Match: ${bestPercentage}%, ` +
          //     `Errors Corrected: ${finalResultWithSuccess.bestMatch.numErrors}, ` +
          //     `Registration Index: ${finalResultWithSuccess.bestMatch.registrationIndex}`
          // );
        }
      } else {
        // addLog(
        //   `❌ Verification FAILED - ${successfulFrames}/${
        //     distancesArray.length
        //   } frames matched (${successRate.toFixed(1)}%), ` +
        //     `Best Match: ${
        //       finalResultWithSuccess.bestMatch
        //         ? finalResultWithSuccess.bestMatch.matchPercentage.toFixed(2)
        //         : "0.00"
        //     }%, ` +
        //     `Required: 100% match OR 50%+ frames must match`
        // );
      }
    } catch (error) {
      // console.error("Verification process error:", error);
      addLog(`Verification FAILED - Error: ${error.message}`);
      setVerificationResult({
        success: false,
        reason: error.message,
      });
    } finally {
      setIsProcessing(false);
    }
  }, [addLog, bchReady, registrationData, salt]);

  function onResults(results) {
    if (!webcamRef.current || !webcamRef.current.video) return;
    if (isProcessing) return; // Don't process if already verifying

    canvasRef.current.width = webcamRef.current.video.videoWidth;
    canvasRef.current.height = webcamRef.current.video.videoHeight;

    const canvasElement = canvasRef.current;
    const canvasCtx = canvasElement.getContext("2d");
    canvasCtx.save();

    canvasCtx.fillStyle = "transparent";
    canvasCtx.fillRect(0, 0, canvasElement.width, canvasElement.height);

    const face_oval = [
      10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379,
      378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
      162, 21, 54, 103, 67, 109,
    ];

    if (results.multiFaceLandmarks) {
      for (const landmarks of results.multiFaceLandmarks) {
        const pixelLandmarks = landmarks.map((marks) => ({
          x: marks.x * canvasRef.current.width,
          y: (1 - marks.y) * canvasRef.current.height,
        }));

        let sumx = 0;
        let sumy = 0;
        pixelLandmarks.forEach((value, index) => {
          if (face_oval.includes(index)) {
            sumx += value.x;
            sumy += value.y;
          }
        });

        const avgx = sumx / face_oval.length;
        const avgy = sumy / face_oval.length;

        const pixelLandmarksCenter = pixelLandmarks.map((marks) => ({
          x: marks.x - avgx,
          y: marks.y - avgy,
        }));

        const sumOfSquares = pixelLandmarksCenter.map(
          (marks) => marks.x ** 2 + marks.y ** 2
        );
        const totalSum = sumOfSquares.reduce((acc, val) => acc + val, 0);
        const scale = Math.sqrt(totalSum / pixelLandmarksCenter.length);
        const normalizedPoints = pixelLandmarksCenter.map((marks) => ({
          x: marks.x / scale,
          y: marks.y / scale,
        }));

        const normalizedPointsFace = normalizedPoints.filter((_, index) =>
          face_oval.includes(index)
        );

        const midlineMandatoryLandmarks = [2, 4, 9];
        const leftMandatoryLandmarks = [70, 107, 46, 55, 33, 133, 98];
        const rightMandatoryLandmarks = [300, 336, 276, 285, 263, 362, 327];

        const mandatoryLandmarks = [
          ...midlineMandatoryLandmarks,
          ...leftMandatoryLandmarks,
          ...rightMandatoryLandmarks,
        ];
        const optionalLandmarks = [
          423, 357, 349, 347, 340, 266, 330, 427, 280, 203, 128, 120, 118, 111,
          36, 101, 207, 50, 187, 147, 411, 376, 336, 107, 351, 399, 429, 363,
          134, 209, 174, 122, 151, 69, 299, 63, 156, 293, 383,
        ];
        const selectedLandmarks = optionalLandmarks.slice(0, 10);

        const getUniquePairs = (arr) => {
          const pairs = [];
          for (let i = 0; i < arr.length; i++) {
            for (let j = i + 1; j < arr.length; j++) {
              pairs.push([Math.min(arr[i], arr[j]), Math.max(arr[i], arr[j])]);
            }
          }
          return pairs;
        };

        const getConsecutivePairs = (arr) => {
          const pairs = [];
          for (let i = 0; i < arr.length; i++) {
            pairs.push([arr[i], arr[(i + 1) % arr.length]]);
          }
          return pairs;
        };

        const getProductPairs = (arr1, arr2) => {
          const pairs = [];
          for (let i = 0; i < arr1.length; i++) {
            for (let j = 0; j < arr2.length; j++) {
              pairs.push([arr1[i], arr2[j]]);
            }
          }
          return pairs;
        };

        const mandatoryLandmarkPairs = [
          ...getUniquePairs(mandatoryLandmarks),
          ...getConsecutivePairs(selectedLandmarks),
          ...getProductPairs(mandatoryLandmarks, selectedLandmarks),
        ];

        const calculatedDistances = calculateLandmarkDistances(
          normalizedPoints,
          mandatoryLandmarkPairs
        );

        const point2 = normalizedPoints[263];
        const point3 = normalizedPoints[33];
        const deltaX = point2.x - point3.x;
        const deltaY = point2.y - point3.y;
        const { pitch, yaw, roll } = computePoseAngles(normalizedPoints[4], [
          deltaX,
          deltaY,
        ]);

        // pitch/yaw/roll tracking removed (was unused and broke build with eslint no-undef)

        drawScatterPlot(
          canvasCtx,
          normalizedPointsFace,
          pitch,
          yaw,
          roll,
          results,
          { color: "white", radius: 2, showIndex: true }
        );

        // Capture 50 frames when conditions are met (with spacing)
        const noseOK = isNoseCentered(results);
        const poseOK = isPoseStable(pitch, yaw, roll);
        const iodOK = computeIOD(pixelLandmarks);

        const now = Date.now();

        // Start capturing if conditions are met and haven't finished capturing all frames
        if (
          noseOK &&
          poseOK &&
          iodOK &&
          frameCountRef.current < TOTAL_FRAMES &&
          !isProcessing
        ) {
          // Start capture process (first frame)
          if (frameCountRef.current === 0 && !hasCapturedRef.current) {
            console.log(
              "✅✅✅ STARTING 50-FRAME CAPTURE FOR VERIFICATION ✅✅✅"
            );
            console.log(
              "Face conditions met - noseOK:",
              noseOK,
              "poseOK:",
              poseOK,
              "iodOK:",
              iodOK
            );
            hasCapturedRef.current = true;
            lastFrameTimeRef.current = now;
            setCaptureProgress(0);
            setCapturedDistances([]); // Reset captured distances array

            // One-time "Verification Workflow" logs (like the table you shared)
            if (!workflowLoggedRef.current) {
              workflowLoggedRef.current = true;

              addLog("Aadhaar Verification Workflow:", "aadhaar");
              addLog("1) User submits fresh face image", "aadhaar");
              addLog("2) Image sent to central server", "aadhaar");
              addLog("3) Server matches against stored biometric", "aadhaar");
              addLog("4) Identity resolved", "aadhaar");
              addLog("Server sees: Face + identity", "aadhaar");
              addLog("Risk: Replay, correlation, central visibility", "aadhaar");

              addLog("Apple Face ID Verification Workflow:", "apple_face_id");
              addLog("1) User presents face", "apple_face_id");
              addLog("2) Matching done fully on-device", "apple_face_id");
              addLog("3) App receives yes/no", "apple_face_id");
              addLog("Server sees: Nothing", "apple_face_id");
              addLog("Limitation: No external auditability", "apple_face_id");

              addLog("MPC Verification Workflow:", "mpc");
              addLog("1) Biometric-derived shares submitted", "mpc");
              addLog("2) Distributed computation", "mpc");
              addLog("3) Match reconstructed across parties", "mpc");
              addLog("Server sees: Partial cryptographic material", "mpc");
              addLog("Risk: Metadata leakage, protocol complexity", "mpc");

              addLog("ByoSync Verification Workflow:", "byosync");
              addLog("1) Server issues nonce (challenge)", "byosync");
              addLog("2) Live face → session-bound proof (device-only)", "byosync");
              addLog("3) Proof sent (non-replayable, non-linkable)", "byosync");
              addLog("4) Server verifies proof without identity", "byosync");
              addLog("Server sees: Stateless verification proof", "byosync");
              addLog("Risk: None beyond session", "byosync");
            }

            // Capture a single photo for Aadhaar panel (once per scan)
            if (!capturedPhotoRef.current && typeof webcamRef.current.getScreenshot === "function") {
              const shot = webcamRef.current.getScreenshot();
              if (shot) {
                capturedPhotoRef.current = true;
                setChannelMeta?.("aadhaar", { photo: shot });
                try {
                  photoHashRef.current = CryptoJS.SHA256(String(shot)).toString();
                } catch (e) {
                  photoHashRef.current = null;
                }
              }
            }
          }

          // Capture frame if enough time has passed (spacing between frames)
          if (
            hasCapturedRef.current &&
            now - lastFrameTimeRef.current >= FRAME_INTERVAL
          ) {
            frameCountRef.current += 1;
            lastFrameTimeRef.current = now;

            // Update Apple Face ID panel with a hash of the current frame distances
            try {
              const hash = CryptoJS.SHA256(JSON.stringify(calculatedDistances)).toString();
              setChannelMeta?.("apple_face_id", {
                distanceHash: hash,
                frame: frameCountRef.current,
              });
            } catch (e) {
              // ignore hashing failures
            }

            // MPC: generate 3 hashes DURING scan time (live)
            try {
              const h1 = CryptoJS.SHA256(JSON.stringify(calculatedDistances)).toString();
              const h2 = photoHashRef.current || CryptoJS.SHA256("no-photo").toString();
              const deviceKeyHash = loadString("deviceKeyHash") || "";
              const h3 = CryptoJS.SHA256(
                `${String(frameCountRef.current)}|${String(deviceKeyHash)}|${String(salt || "")}`
              ).toString();
              setChannelMeta?.("mpc", {
                hash1: h1,
                hash2: h2,
                hash3: h3,
                frame: frameCountRef.current,
              });
            } catch (e) {
              // ignore hashing failures
            }

            // Add this frame's distances to the array
            // Use functional update to ensure we get the latest state
            setCapturedDistances((prev) => {
              // Create a new array with the new frame
              const newArray = [...prev, calculatedDistances];
              const currentLength = newArray.length;

              // Update progress immediately
              setCaptureProgress(currentLength);

              // console.log(`📸 Frame ${currentLength}/${TOTAL_FRAMES} captured`);
              // console.log(
              //   `📊 Current capturedDistances length: ${currentLength}`
              // );
              // console.log(`📊 Previous length was: ${prev.length}`);

              // If we've captured all frames, log completion
              if (currentLength >= TOTAL_FRAMES) {
                // console.log(
                //   `✅✅✅ ALL ${TOTAL_FRAMES} FRAMES CAPTURED ✅✅✅`
                // );
                // console.log(
                //   "Waiting for BCH and registration data to start verification..."
                // );
              }

              return newArray;
            });
          }
        }

        // ear tracking removed (was unused and broke build with eslint no-undef)
      }
    }

    canvasCtx.restore();
  }

  // Keep the latest onResults handler without forcing the FaceMesh init effect to re-run.
  // (Assigning to a ref during render is safe and avoids exhaustive-deps warnings.)
  onResultsRef.current = onResults;

  // Initialize BCH module
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 30; // Increased retries
    let timeoutId = null;
    let isInitialized = false;

    const initBCHModule = async () => {
      // Prevent multiple simultaneous initializations
      if (isInitialized || bchReady) {
        return;
      }

      try {
        retryCount++;
        // console.log(
        //   `🔄 Attempting BCH initialization (attempt ${retryCount}/${maxRetries})...`
        // );
        // console.log("window.BCHBiometric:", typeof window.BCHBiometric);
        // console.log("window object:", typeof window);

        // Check if BCHBiometric is available
        if (typeof window !== "undefined" && window.BCHBiometric) {
          // console.log(
          //   "✅ window.BCHBiometric found:",
          //   Object.keys(window.BCHBiometric)
          // );

          if (typeof window.BCHBiometric.initBCH === "function") {
            // console.log("📞 Calling window.BCHBiometric.initBCH()...");
            try {
              isInitialized = true; // Set flag before async call
              await window.BCHBiometric.initBCH();
              setBchReady(true);
              // console.log("✅✅✅ BCH module initialized successfully!");
              return; // Success, exit
            } catch (initError) {
              // console.error("❌ Error calling initBCH:", initError);
              isInitialized = false; // Reset flag on error
              throw initError;
            }
          } else {
            // console.warn("⚠️ window.BCHBiometric.initBCH is not a function");
            // console.log("Available methods:", Object.keys(window.BCHBiometric));
          }
        } else {
          // console.warn("⚠️ window.BCHBiometric not found");
          // console.log("Checking if bch_integration.js loaded...");
          // Check if the file is being loaded
          if (typeof window !== "undefined") {
            // console.log("window exists, checking for BCHBiometric...");
          }
        }

        // If we get here, retry
        if (retryCount < maxRetries) {
          // console.warn(
          //   `⏳ Retrying BCH initialization in 500ms... (${retryCount}/${maxRetries})`
          // );
          timeoutId = setTimeout(initBCHModule, 500);
        } else {
          // console.error("❌ Failed to initialize BCH module after max retries");
          // console.error(
          //   "Please ensure bch_integration.js is properly imported at the top of the file"
          // );
          // console.error("Expected: import '../bch_integration';");
        }
      } catch (error) {
        // console.error("❌ Failed to initialize BCH module:", error);
        isInitialized = false;
        if (retryCount < maxRetries) {
          timeoutId = setTimeout(initBCHModule, 500);
        }
      }
    };

    // Start initialization immediately and also after delays
    initBCHModule();
    const timer1 = setTimeout(() => {
      if (!bchReady) initBCHModule();
    }, 100);
    const timer2 = setTimeout(() => {
      if (!bchReady) initBCHModule();
    }, 500);
    const timer3 = setTimeout(() => {
      if (!bchReady) initBCHModule();
    }, 1000);
    const timer4 = setTimeout(() => {
      if (!bchReady) initBCHModule();
    }, 2000);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [bchReady]);

  // Load salt and registration data: Check localStorage first (once), then API if needed
  useEffect(() => {
    // Prevent multiple runs
    if (dataLoadedRef.current) return;
    dataLoadedRef.current = true;

    const loadData = async () => {
      try {
        // Step 1: Check localStorage first
        const storedSalt = localStorage.getItem("Salt");
        const rawData = localStorage.getItem("bchRegistrationData");

        let saltFound = false;
        let dataFound = false;

        // Load salt from localStorage if available
        if (storedSalt) {
          try {
            const parsedSalt = JSON.parse(storedSalt);
            setSalt(parsedSalt);
            saltFound = true;
            console.log("✅ Salt loaded from localStorage");
            addLog("✅ Salt loaded from localStorage");
          } catch (parseError) {
            console.error("Error parsing salt:", parseError);
          }
        }

        // Load registration data from localStorage if available
        if (rawData) {
          try {
            const data = JSON.parse(rawData);
            if (data && Array.isArray(data) && data.length > 0) {
              setRegistrationData(data);
              dataFound = true;
              console.log(
                "✅ Registration data loaded from localStorage:",
                data.length,
                "face record(s)"
              );
            }
          } catch (parseError) {
            console.error("Error parsing registration data:", parseError);
          }
        }

        // Step 2: If anything is missing, load from API
        if (!saltFound || !dataFound) {
          console.log("⚠️ Missing data in localStorage, loading from API...");
          addLog("⚠️ Loading missing data from API...");

          try {
            // Get deviceKeyHash from localStorage
            const deviceKeyHash = loadString("deviceKeyHash");

            if (!deviceKeyHash) {
              addLog("❌ Failed to get device key hash");
              return;
            }

            // Call API to get face registration data and salt
            const response = await api.post(`/users/getFaceId`, {
              deviceKeyHash,
            });

            console.log("API Response:", response.data);

            // Extract faceData and salt from response
            const faceData =
              response.data?.faceData ||
              response.data?.data?.faceData ||
              response.data;
            const apiSalt =
              response.data?.salt || response.data?.data?.salt || null;

            // Set salt from API if not already loaded from localStorage
            if (apiSalt && !saltFound) {
              setSalt(apiSalt);
              localStorage.setItem("Salt", JSON.stringify(apiSalt));
              console.log("✅ Salt loaded from API and saved to localStorage");
              addLog("✅ Salt loaded from API and saved");
            }

            // Set registration data from API if not already loaded from localStorage
            if (faceData && !dataFound) {
              const registrationArray = Array.isArray(faceData)
                ? faceData
                : [faceData];

              if (registrationArray.length > 0) {
                setRegistrationData(registrationArray);
                localStorage.setItem(
                  "bchRegistrationData",
                  JSON.stringify(registrationArray)
                );
                console.log(
                  "✅ Registration data loaded from API:",
                  registrationArray.length,
                  "face record(s)"
                );
                addLog(
                  `✅ Registration data loaded from API: ${registrationArray.length} face record(s)`
                );
              } else {
                addLog("⚠️ No face registration data found for this device");
              }
            } else if (!faceData && !dataFound) {
              addLog("⚠️ API response does not contain face data");
            }
          } catch (error) {
            console.error("Error loading from API:", error);
            addLog(`API Error: ${error.message}`);
            if (error.response) {
              addLog(
                `API Error: ${error.response.status} - ${JSON.stringify(
                  error.response.data
                )}`
              );
            }
          }
        }
      } catch (error) {
        console.error("Error in loadData:", error);
        addLog(`Error loading data: ${error.message}`);
      }
    };

    // Run once on mount
    loadData();
  }, [addLog]);

  // Removed isScanning logic - capture happens directly when face conditions are met
  // This useEffect just logs status for debugging
  useEffect(() => {
    // console.log("📊 Current status:", {
    //   bchReady,
    //   hasRegistrationData: !!registrationData,
    //   registrationDataLength: registrationData?.length || 0,
    //   capturedDistances: !!capturedDistances,
    //   isProcessing,
    // });
  }, [bchReady, registrationData, capturedDistances, isProcessing]);

  useEffect(() => {
    if (capturedDistances && capturedDistances.length > 0) {
      // console.log(
      //   "📊 capturedDistances updated - frames:",
      //   capturedDistances.length
      // );
      // Sync progress with actual captured distances length
      if (capturedDistances.length !== captureProgress) {
        // console.log(
        //   `📊 Syncing progress: ${captureProgress} -> ${capturedDistances.length}`
        // );
        setCaptureProgress(capturedDistances.length);
      }
    }
  }, [capturedDistances, captureProgress]);

  useEffect(() => {
    // console.log("registrationData", registrationData);
    // console.log("capturedDistances", capturedDistances);
    // console.log("isProcessing", isProcessing);
    // console.log("bchReady", bchReady);
  }, [registrationData, capturedDistances, isProcessing, bchReady]);

  // Trigger verification when all frames are captured - process sequentially
  useEffect(() => {
    // Start verification only after ALL frames are captured
    // Then process them sequentially, updating result after each frame
    if (
      capturedDistances &&
      Array.isArray(capturedDistances) &&
      capturedDistances.length === TOTAL_FRAMES &&
      !isProcessing &&
      !verificationResult
    ) {
      // console.log("🔍 Verification trigger check:", {
      // hasDistances: true,
      // distancesLength: capturedDistances.length,
      // expectedFrames: TOTAL_FRAMES,
      // allFramesCaptured: capturedDistances.length === TOTAL_FRAMES,
      // bchReady,
      // hasRegData: !!registrationData && registrationData.length > 0,
      // isProcessing,
      // hasResult: !!verificationResult,
      // });

      // Check if we have registration data (try localStorage if state is empty)
      let currentRegData = null;

      if (registrationData && registrationData.length > 0) {
        currentRegData = registrationData;
        // console.log(
        //   "Using registration data from state:",
        //   currentRegData.length
        // );
      } else {
        // Registration data should be loaded from localStorage
        // console.warn(
        //   "⚠️ Registration data is empty, waiting for localStorage to load..."
        // );
        addLog(
          "⚠️ Waiting for face registration data to load from localStorage..."
        );
        return; // Don't proceed without registration data
      }

      // Check if salt is available
      if (!salt) {
        addLog("⚠️ Waiting for salt to load from localStorage...");
        return; // Don't proceed without salt
      }

      // If BCH is ready and we have registration data and salt, start verification
      if (bchReady && currentRegData && currentRegData.length > 0 && salt) {
        // console.log("✅✅✅ ALL CONDITIONS MET - STARTING VERIFICATION ✅✅✅");
        // console.log(
        //   "Distances:",
        //   capturedDistances.length,
        //   "Registration records:",
        //   currentRegData.length
        // );
        setIsProcessing(true);

        // Call verification
        processVerification(capturedDistances)
          .then(() => {
            // console.log("✅ Verification completed");
          })
          .catch((error) => {
            // console.error("❌ Error in processVerification:", error);
            addLog(`Verification FAILED - Error: ${error.message}`);
            setIsProcessing(false);
          });
      } else {
        if (!bchReady) {
          // console.log(
          //   "⏳ Waiting for BCH to be ready (current:",
          //   bchReady,
          //   ")..."
          // );
        }
        if (!currentRegData || currentRegData.length === 0) {
          // console.log(
          //   "⏳ Waiting for registration data (current:",
          //   currentRegData?.length || 0,
          //   ")..."
          // );
        }
        if (!salt) {
          // console.log("⏳ Waiting for salt to load...");
        }
      }
    } else {
      if (!capturedDistances || capturedDistances.length === 0) {
        // console.log("⏳ Waiting for distances to be captured...");
      }
      if (isProcessing) {
        // console.log("⏳ Already processing verification...");
      }
      if (verificationResult && verificationResult.isComplete) {
        // console.log("✅ Verification already completed");
      }
    }
  }, [
    capturedDistances,
    captureProgress,
    bchReady,
    registrationData,
    isProcessing,
    verificationResult,
    salt,
    addLog,
    processVerification,
  ]);

  // Auto-retry after 5 seconds if verification fails
  // This will restart face_mesh scanning and automatically trigger verification again
  useEffect(() => {
    const handleVerificationFailure = () => {
      // Log once per failure outcome (pre-retry)
      if (!outcomeLoggedRef.current.failure) {
        outcomeLoggedRef.current.failure = true;
        addLog("User verification FAILED", "byosync");
        addLog("User verification FAILED", "apple_face_id");
        addLog("User verification FAILED", "mpc");
        addLog("User verification FAILED", "aadhaar");
      }

      const retryTimer = setTimeout(() => {
        // console.log("🔄 Resetting for auto-retry - restarting face scan...");
        // addLog("🔄 Resetting scan - restarting face detection...");

        // Reset all capture states to allow new scan
        setCapturedDistances([]);
        setVerificationResult(null);
        setCaptureProgress(0);
        setIsProcessing(false);
        hasCapturedRef.current = false;
        frameCountRef.current = 0;
        lastFrameTimeRef.current = 0;
        capturedPhotoRef.current = false;
        workflowLoggedRef.current = false;
        outcomeLoggedRef.current = { success: false, failure: false };

        // console.log(
        //   "✅ Reset complete. Face mesh will restart scanning automatically."
        // );
        // console.log("📸 Waiting for face detection to capture new frames...");
        // addLog("✅ Ready for new scan. Please position your face.");
        // addLog(
        //   "📸 Face detection active - scanning will start automatically when face is detected."
        // );
      }, 3000); // 3 seconds delay

      // Cleanup timer if component unmounts or verification succeeds
      return () => clearTimeout(retryTimer);
    };

    const handleVerificationSuccess = async () => {
      try {
        // Ensure outcome logs happen BEFORE redirect and appear in ALL panels
        if (!outcomeLoggedRef.current.success) {
          outcomeLoggedRef.current.success = true;
          addLog("Face match VERIFIED. Redirecting to profile...", "byosync");
          addLog("User verification VERIFIED", "apple_face_id");
          addLog("User verification VERIFIED", "mpc");
          addLog("User verification VERIFIED", "aadhaar");
        }

        const rawUserId = localStorage.getItem("userId");
        // Backward-compatible: old builds stored JSON.stringify("id") which becomes "\"id\""
        let userId = rawUserId;
        try {
          userId = JSON.parse(rawUserId);
        } catch (e) {
          // keep raw string
        }
        const deviceKeyHash = loadString("deviceKeyHash");

        const response = await api.get(
          `/users/get-user-data-by-id?userId=${userId}&deviceKeyHash=${deviceKeyHash}`
        );
        //    console.log("response", response);
        // console.log("response.data.data", response.data.data);
        localStorage.setItem("userData", JSON.stringify(response.data.data));
        localStorage.setItem("user", "true"); // Backup flag for older checks if any
        navigate("/profile");
      } catch (err) {
        addLog(`API Error: ${err.message}`);
      }
    };

    if (verificationResult) {
      if (verificationResult.isComplete && !verificationResult.success) {
        handleVerificationFailure();
      } else if (verificationResult.success) {
        handleVerificationSuccess();
      }
    }
  }, [verificationResult, navigate, addLog]);

  // Setup FaceMesh
  useEffect(() => {
    const faceMesh = new FaceMesh({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
      },
    });

    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
      selfieMode: true,
    });

    faceMesh.onResults((results) => {
      onResultsRef.current?.(results);
    });

    if (
      typeof webcamRef.current !== "undefined" &&
      webcamRef.current !== null
    ) {
      cameraRef.current = new cam.Camera(webcamRef.current.video, {
        onFrame: async () => {
          await faceMesh.send({ image: webcamRef.current.video });
        },
        width: "100%",
        height: "100%",
      });
      cameraRef.current.start();
    }

    return () => {
      try {
        cameraRef.current?.stop?.();
      } catch (e) {
        // ignore cleanup errors
      }
    };
  }, []);

  // Load arrow images
  useEffect(() => {
    const loadImage = (imgRef, src) => {
      imgRef.current = new Image();
      imgRef.current.onload = () => {
        console.log(`Image loaded successfully: ${src}`);
      };
      imgRef.current.onerror = (error) => {
        console.error(`Failed to load image: ${src}`, error);
        // Set a flag to indicate the image is broken
        imgRef.current = null;
      };
      imgRef.current.src = src;
    };

    loadImage(
      leftArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/teh2zqezgiig0hazmacv.png"
    );
    loadImage(
      rightArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/uvhrqf4pv5whhs4iqrse.png"
    );
    loadImage(
      rollleftArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/xbrveszed0qkpze4mxoz.png"
    );
    loadImage(
      rollrightArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/hrfus7kjanpvvg996trw.png"
    );
    loadImage(
      upArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/opicmo7rtqn2siyn543e.png"
    );
    loadImage(
      downArrowImage,
      "https://res.cloudinary.com/deepak2199/image/upload/v1768561055/yydbtemfa5derghpu2z6.png"
    );
  }, []);

  return (
    <div
      className="left-section-scan"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        minHeight: "100vh",
        color: "white",
        padding: "20px",
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        boxSizing: "border-box",
        gap: "20px",
      }}
    >
      {/* Top - Instructions */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "20px",
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        {(iod < 130 || iod > 133) && (
          <div
            style={{
              padding: "12px 24px",
              background: "rgba(255, 193, 7, 0.15)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 193, 7, 0.4)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              animation: "pulse 2s infinite",
              boxShadow: "0 4px 20px rgba(255, 193, 7, 0.3)",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#ffc107",
                boxShadow: "0 0 10px #ffc107",
              }}
            />
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#ffc107",
                textShadow: "0 0 10px rgba(255, 193, 7, 0.5)",
              }}
            >
              {iod < 130 ? "📏 Move closer" : iod > 133 ? "📏 Move away" : ""}
            </span>
          </div>
        )}

        {!center && (
          <div
            style={{
              padding: "12px 24px",
              background: "rgba(255, 193, 7, 0.15)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 193, 7, 0.4)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              animation: "pulse 2s infinite",
              boxShadow: "0 4px 20px rgba(255, 193, 7, 0.3)",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#ffc107",
                boxShadow: "0 0 10px #ffc107",
              }}
            />
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#ffc107",
                textShadow: "0 0 10px rgba(255, 193, 7, 0.5)",
              }}
            >
              {"Nose tip on Red Dot"}
            </span>
          </div>
        )}
      </div>

      {/* Center - Camera Frame */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          height: "auto",
          maxHeight: "calc(100vh - 200px)",
          aspectRatio: "16 / 9",
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow:
            "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 188, 212, 0.3)",
          border: "2px solid transparent",
          background:
            "linear-gradient(135deg, rgba(0, 188, 212, 0.1), rgba(0, 229, 255, 0.1))",
        }}
      >
        <Webcam
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: "scaleX(-1)",
          }}
        />
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "transparent",
            zIndex: 9,
            transform: "scaleX(-1)",
          }}
        ></canvas>
      </div>

      {/* Capture Progress Bar */}
      {/* {captureProgress > 0 && captureProgress <= TOTAL_FRAMES && (
        <div
          style={{
            width: "90%",
            maxWidth: "600px",
            padding: "16px 32px",
            background: "rgba(128, 128, 128, 0.9)",
            backdropFilter: "blur(10px)",
            border: "1px solid #00bcd4",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.9)",
                fontWeight: "500",
              }}
            >
              Progress
            </span>
            <span
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#00bcd4",
              }}
            >
              {captureProgress} / {TOTAL_FRAMES}
            </span>
          </div>
          <div
            style={{
              width: "100%",
              height: "8px",
              background: "rgba(255, 255, 255, 0.2)",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${(captureProgress / TOTAL_FRAMES) * 100}%`,
                height: "100%",
                background: "linear-gradient(90deg, #00bcd4, #00e5ff)",
                borderRadius: "4px",
                transition: "width 0.2s ease",
                boxShadow: "0 0 10px rgba(0, 188, 212, 0.5)",
              }}
            />
          </div>
        </div>
      )} */}

      {/* Status Messages */}
      {!bchReady && (
        <div
          style={{
            padding: "12px 24px",
            background: "rgba(255, 193, 7, 0.15)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 193, 7, 0.4)",
            borderRadius: "12px",
            fontSize: "16px",
            fontWeight: "600",
            color: "#ffc107",
          }}
        >
          Initializing BCH module...
        </div>
      )}

      {bchReady && (!registrationData || registrationData.length === 0) && (
        <div
          style={{
            padding: "12px 24px",
            background: "rgba(255, 0, 0, 0.15)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 0, 0, 0.4)",
            borderRadius: "12px",
            fontSize: "16px",
            fontWeight: "600",
            color: "#ff0000",
          }}
        >
          No registration data found. Please register first.
        </div>
      )}

      {bchReady &&
        registrationData &&
        registrationData.length > 0 &&
        !capturedDistances &&
        !isProcessing && (
          <div
            style={{
              padding: "12px 24px",
              background: "rgba(0, 188, 212, 0.15)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(0, 188, 212, 0.4)",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: "600",
              color: "#00bcd4",
            }}
          >
            Ready for verification. Position your face in the frame.
          </div>
        )}

      {isProcessing && (
        <div
          style={{
            padding: "16px 32px",
            fontSize: "18px",
            fontWeight: "600",
            color: "#00bcd4",
          }}
        >
          Processing Verification...
        </div>
      )}

      {/* Verification Result */}
      {verificationResult && (
        <div
          style={{
            width: "100%",
            maxWidth: "600px",
            padding: "24px",
            background: verificationResult.isComplete
              ? verificationResult.success
                ? "rgba(0, 255, 0, 0.1)"
                : "rgba(255, 0, 0, 0.1)"
              : "rgba(0, 188, 212, 0.1)",
            border: `2px solid ${verificationResult.isComplete
                ? verificationResult.success
                  ? "#00ff00"
                  : "#ff0000"
                : "#00bcd4"
              }`,
            borderRadius: "16px",
            textAlign: "center",
          }}
        >
          <h3
            style={{
              color: verificationResult.isComplete
                ? verificationResult.success
                  ? "#00ff00"
                  : "#ff0000"
                : "#00bcd4",
              margin: "0 0 16px 0",
            }}
          >
            {verificationResult.isComplete
              ? verificationResult.success
                ? "✓ Verification Successful"
                : "✗ Verification Failed"
              : `⏳ Processing... (${verificationResult.processedFrames || 0}/${verificationResult.totalFrames
              })`}
          </h3>
          {verificationResult.bestMatch && (
            <div>
              <p>
                <strong>Best Match Percentage:</strong>{" "}
                {verificationResult.bestMatch.matchPercentage.toFixed(2)}%
              </p>
              <p>
                <strong>Frames Matched:</strong>{" "}
                {verificationResult.successfulFrames} /{" "}
                {verificationResult.totalFrames} (
                {verificationResult.successRate.toFixed(1)}%)
              </p>
              {/* <p>
                <strong>Errors Corrected:</strong>{" "}
                {verificationResult.bestMatch.numErrors}
              </p>
              <p>
                <strong>Registration Index:</strong>{" "}
                {verificationResult.bestMatch.registrationIndex}
              </p> */}
            </div>
          )}
          {!verificationResult.isComplete && (
            <p style={{ color: "#00bcd4", marginTop: "16px" }}>
              Processing frames in background...
            </p>
          )}
          {verificationResult.reason && <p>{verificationResult.reason}</p>}
        </div>
      )}

      {/* Add CSS animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
}

export default VerifyScan;
